(() => {
  'use strict';

  /** Report errors to Rollbar (DryerKitFilter). */
  function dkRollbar(context, err, extra) {
    if (!window.Rollbar) return;
    try {
      const message =
        err && typeof err === 'object' && err.message
          ? String(err.message)
          : typeof err === 'string'
            ? err
            : 'DryerKitFilter error';
      Rollbar.error(message, err instanceof Error ? err : { detail: err }, {
        context: 'DryerKitFilter.' + context,
        ...(extra && typeof extra === 'object' ? extra : {}),
      });
    } catch (_) {}
  }

  class DryerKitFilter {
    constructor() {
      try {
      // CRITICAL: Prevent multiple instances
      if (window.dryerKitFilterInstance && window.dryerKitFilterInstance.initialized) {
        // console.log('[DryerKitFilter] Instance already exists, skipping duplicate initialization');
        return;
      }
      
      this.modelSelect = document.querySelector('[data-dryer-model]');
      this.serialSelect = document.querySelector('[data-serial-number]');
      this.modelList = document.querySelector('[data-dryer-model-list]');
      this.serialList = document.querySelector('[data-serial-number-list]');
      this.modelToggle = document.querySelector('#dryer-model-toggle');
      this.serialToggle = document.querySelector('#serial-number-toggle');
      
      if (!this.modelSelect || !this.serialSelect) {
        return;
      }

      this.submitBtn = document.querySelector('[data-dryer-kit-submit]');
      this.clearBtn = document.querySelector('[data-dryer-kit-clear]');
      this.selectedModel = null;
      this.selectedSerial = null;
      this.serialMap = {};
      this.productMap = {}; // Store model|serial -> productHandle mapping
      this.allDryerKits = [];
      this.originalProductCount = null; // Store original total products count
      this.sortChangedWithFilter = false; // Flag to track if sort changed with active filter
      this.allProducts = []; // Store all products from all pages for cross-page search
      this.isLoadingProducts = false; // Track if products are being loaded
      this.productsPerPage = 24; // Default products per page
      this.isShowingLoadingMessage = false; // Prevent duplicate loading messages
      this.isShowingNoMatchMessage = false; // Prevent duplicate no-match messages
      this.messageUniqueId = 0; // Unique ID for each message to prevent duplicates
      this.isSearching = false; // CRITICAL: Prevent concurrent searches
      this.isUpdatingUrl = false; // CRITICAL: Flag to prevent re-search when WE update URL
      this.lastSearchParams = null; // Track last search to prevent duplicate "no product" messages
      this.noProductShownForParams = null; // Track params for which "no product" was already shown
      this.initialized = true; // Mark as initialized

      this.init();
      } catch (e) {
        dkRollbar('constructor', e);
      }
    }

    async init() {
      await this.loadDryerKitData();
      this.bindEvents();
      this.bindSubmitEvent();
      this.bindClearEvent();
      
      // Store original product count before applying any filters
      this.storeOriginalProductCount();
      
      this.applyFilterFromUrl();
      
      // Listen for AJAX page updates (sorting, pagination, etc.)
      this.listenForPageUpdates();
    }
    
    listenForPageUpdates() {
      // Helper function to re-apply filter from URL
      const reapplyFilterFromUrl = async () => {
        try {
        // CRITICAL: Skip if we're the ones updating the URL (prevents loop)
        if (this.isUpdatingUrl) {
          // console.log('[DryerKitFilter] reapplyFilterFromUrl: Skipping - we are updating URL');
          return;
        }
        
        // CRITICAL: Skip if already searching
        if (this.isSearching) {
          // console.log('[DryerKitFilter] reapplyFilterFromUrl: Skipping - already searching');
          return;
        }
        
        // ONLY read from URL - do not use instance variables
        // URL should only be updated when submit button is clicked
        const currentUrl = new URL(window.location.href);
        const productHandle = currentUrl.searchParams.get('product_handle');
        const dryerModel = currentUrl.searchParams.get('dryer_model');
        const dryerSerial = currentUrl.searchParams.get('dryer_serial');
        
        // console.log('[DryerKitFilter] Re-applying filter from URL:', { productHandle, dryerModel, dryerSerial });
        
        // CRITICAL: Check if we already showed "no product" for these exact params
        // Key format matches showNoProductMessage: model|serial
        const searchKey = `${dryerModel}|${dryerSerial}`;
        if (this.noProductShownForParams === searchKey) {
          // console.log('[DryerKitFilter] reapplyFilterFromUrl: Skipping - "no product" already shown for these params:', searchKey);
          return;
        }
        
        // Only reapply if URL has all required parameters
        // Do NOT fallback to instance variables - URL is the source of truth
        if (productHandle && dryerModel && dryerSerial) {
          // Update instance variables from URL (for display purposes only)
          this.selectedModel = dryerModel;
          this.selectedSerial = dryerSerial;
          
          // Store original count if not already stored
          if (this.originalProductCount === null) {
            this.storeOriginalProductCount();
          }
          
          // Check if products listing exists
          const productsListing = document.querySelector('#products-listing');
          if (!productsListing) {
            // Wait a bit more if products listing doesn't exist yet
            setTimeout(() => {
              reapplyFilterFromUrl();
            }, 300);
            return;
          }
          
          // Check if grid is still loading
          const gridsContainer = document.querySelector('.collection-filters-container, [data-section-id]');
          if (gridsContainer && gridsContainer.classList.contains('loading')) {
            // Wait for loading to complete
            const checkLoading = setInterval(() => {
              if (!gridsContainer.classList.contains('loading')) {
                clearInterval(checkLoading);
                setTimeout(async () => {
                  // Skip URL update when re-applying from URL
                  await this.filterProductsByHandle(productHandle, true);
                }, 200);
              }
            }, 100);
            // Timeout after 5 seconds
            setTimeout(() => clearInterval(checkLoading), 5000);
            return;
          }
          
          // Delay to ensure DOM is fully ready and rendered
          // Use multiple attempts to ensure filter is applied
          let attempts = 0;
          const maxAttempts = 5; // Increased attempts
          
          const tryApplyFilter = async () => {
            attempts++;
            const productsListing = document.querySelector('#products-listing');
            const productCards = productsListing ? productsListing.querySelectorAll('[data-product-card]') : [];
            
            // Check if grid is still loading
            const gridsContainer = document.querySelector('.collection-filters-container, [data-section-id]');
            const isLoading = gridsContainer && gridsContainer.classList.contains('loading');
            
            if ((productCards.length > 0 && !isLoading) || attempts >= maxAttempts) {
              // Products are loaded and not loading, or we've tried enough times
              // console.log(`[DryerKitFilter] Applying filter (attempt ${attempts}/${maxAttempts})`);
              // Pass flag to skip URL update when re-applying from URL
              await this.filterProductsByHandle(productHandle, true);
            } else {
              // Wait a bit more and try again
              // console.log(`[DryerKitFilter] Waiting for products to load (attempt ${attempts}/${maxAttempts})...`);
              setTimeout(tryApplyFilter, 300); // Increased delay
            }
          };
          
          setTimeout(tryApplyFilter, 500); // Increased initial delay
        }
        } catch (e) {
          dkRollbar('reapplyFilterFromUrl', e);
        }
      };

      // Listen for Shopify AJAX section loads (sorting triggers this)
      document.addEventListener('shopify:section:load', async () => {
        await reapplyFilterFromUrl();
      });
      
      // Listen for custom grid update event (dispatched by storefront-filters.js)
      document.addEventListener('collection:grid:updated', (event) => {
        // console.log('[DryerKitFilter] Grid updated event received, re-applying filter...', event.detail);
        
        // Always check if dryer filter should be active (from URL or instance)
        const urlParams = new URLSearchParams(window.location.search);
        const hasDryerParams = urlParams.get('product_handle') && 
                               urlParams.get('dryer_model') && 
                               urlParams.get('dryer_serial');
        const hasInstanceFilter = this.selectedModel && this.selectedSerial;
        
        if (hasDryerParams || hasInstanceFilter || this.sortChangedWithFilter) {
          this.sortChangedWithFilter = false; // Reset flag
          // Wait a bit longer to ensure grid is fully rendered
          setTimeout(async () => {
            await reapplyFilterFromUrl();
          }, 300);
        }
      });
      
      // Direct listener for sort changes - use event delegation since inputs might not exist yet
      document.addEventListener('change', (e) => {
        if (e.target && e.target.name === 'sort_by') {
          // Check if dryer filter is active
          const urlParams = new URLSearchParams(window.location.search);
          const hasDryerFilter = urlParams.get('product_handle') && urlParams.get('dryer_model') && urlParams.get('dryer_serial');
          
          if (hasDryerFilter || (this.selectedModel && this.selectedSerial)) {
            // console.log('[DryerKitFilter] Sort changed with active filter, will re-apply after grid updates...');
            // The grid update event will handle the re-application
            // But also set a flag to ensure we re-apply
            this.sortChangedWithFilter = true;
          }
        }
      });
      
      // Also listen for custom events that might be dispatched
      document.addEventListener('collection:updated', async () => {
        await reapplyFilterFromUrl();
      });
      
      // Listen for URL hash changes (some sorting might use this)
      window.addEventListener('hashchange', async () => {
        await reapplyFilterFromUrl();
      });
      
      // Listen for popstate (back/forward navigation)
      window.addEventListener('popstate', async () => {
        await reapplyFilterFromUrl();
      });
      
      // Override history.pushState to catch URL changes
      // Only intercept if it's not from storefront-filters (to avoid conflicts)
      const originalPushState = history.pushState;
      const self = this;
      history.pushState = function(...args) {
        originalPushState.apply(history, args);
        
        // Check if this pushState is from storefront-filters (it will have searchParams in state)
        const state = args[0];
        const isFromStorefrontFilters = state && state.searchParams;
        
        // Small delay to let the page update
        setTimeout(async () => {
          const urlParams = new URLSearchParams(window.location.search);
          if (urlParams.get('product_handle') && urlParams.get('dryer_model') && urlParams.get('dryer_serial')) {
            // console.log('[DryerKitFilter] History pushState detected with dryer params, re-applying filter...', { isFromStorefrontFilters });
            
            // If from storefront-filters, wait for grid update event instead
            // Otherwise, apply filter directly
            if (!isFromStorefrontFilters) {
              setTimeout(async () => {
                await reapplyFilterFromUrl();
              }, 400);
            }
            // If from storefront-filters, the collection:grid:updated event will handle it
          }
        }, 100);
      };
      
      // Fallback: Check if filter should be active but isn't applied
      // Only check when URL changes, not continuously
      let lastCheckedUrl = window.location.href;
      setInterval(() => {
        const currentUrl = window.location.href;
        if (currentUrl !== lastCheckedUrl) {
          lastCheckedUrl = currentUrl;
          
          const urlParams = new URLSearchParams(window.location.search);
          const productHandle = urlParams.get('product_handle');
          const dryerModel = urlParams.get('dryer_model');
          const dryerSerial = urlParams.get('dryer_serial');
          
          if (productHandle && dryerModel && dryerSerial) {
            // Wait a bit for grid to update, then check
            setTimeout(() => {
              const productsListing = document.querySelector('#products-listing');
              if (productsListing) {
                const allCards = productsListing.querySelectorAll('[data-product-card]');
                const visibleCards = Array.from(allCards).filter(card => {
                  const style = window.getComputedStyle(card);
                  return style.display !== 'none' && !card.classList.contains('dryer-kit-hidden');
                });
                
                // If we have multiple visible products but filter should be active, re-apply
                if (visibleCards.length > 1) {
                  const matchingProduct = visibleCards.find(card => {
                    const handle = (card.getAttribute('data-product-handle') || '').trim();
                    return handle === productHandle.trim();
                  });
                  
                  if (!matchingProduct) {
                    // Filter not applied correctly, re-apply
                    // console.log('[DryerKitFilter] Fallback: Multiple products visible but filter should be active, re-applying...');
                    reapplyFilterFromUrl(); // Don't await - let it run
                  }
                }
              }
            }, 1000);
          }
        }
      }, 1000); // Check every second for URL changes
      
      // Also use MutationObserver to watch for product grid changes
      if (typeof MutationObserver !== 'undefined') {
        const productsListing = document.querySelector('#products-listing');
        if (productsListing) {
          const observer = new MutationObserver((mutations) => {
            let hasProductChanges = false;
            let loadingRemoved = false;
            
            mutations.forEach((mutation) => {
              // Check for product card changes
              if (mutation.addedNodes.length > 0 || mutation.removedNodes.length > 0) {
                mutation.addedNodes.forEach((node) => {
                  if (node.nodeType === 1) {
                    if (node.hasAttribute && node.hasAttribute('data-product-card')) {
                      hasProductChanges = true;
                    } else if (node.querySelector && node.querySelector('[data-product-card]')) {
                      hasProductChanges = true;
                    }
                    // Also check if the grid itself was replaced
                    if (node.id === 'products-listing' || node.querySelector('#products-listing')) {
                      hasProductChanges = true;
                    }
                  }
                });
              }
              
              // Check if loading class was removed (AJAX update complete)
              if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                const target = mutation.target;
                if (target.classList && !target.classList.contains('loading')) {
                  // Check if it was previously loading
                  if (mutation.oldValue && mutation.oldValue.includes('loading')) {
                    loadingRemoved = true;
                  }
                }
              }
            });
            
            // Re-apply filter if products changed or loading finished
            if (hasProductChanges || loadingRemoved) {
              // Debounce to avoid multiple calls
              clearTimeout(this.reapplyFilterTimeout);
              this.reapplyFilterTimeout = setTimeout(async () => {
                await reapplyFilterFromUrl();
              }, 300);
            }
          });
          
          observer.observe(productsListing, { 
            childList: true, 
            subtree: true,
            attributes: true,
            attributeOldValue: true
          });
          
          // Also observe the grids container if it exists
          const gridsContainer = document.querySelector('[data-products-grid]');
          if (gridsContainer && gridsContainer !== productsListing) {
            observer.observe(gridsContainer, {
              childList: true,
              subtree: true,
              attributes: true,
              attributeOldValue: true
            });
          }
          
          // Also observe the main container for loading class changes
          const mainContainer = document.querySelector('.collection-filters-container, [data-section-id]');
          if (mainContainer) {
            observer.observe(mainContainer, {
              attributes: true,
              attributeFilter: ['class'],
              attributeOldValue: true
            });
          }
        }
      }
    }

    async applyFilterFromUrl() {
      // Apply filter on page load if URL parameters are present
      // This ensures filtering works with pagination and direct links
      const urlParams = new URLSearchParams(window.location.search);
      const productHandle = urlParams.get('product_handle');
      const dryerModel = urlParams.get('dryer_model');
      const dryerSerial = urlParams.get('dryer_serial');
      
      if (productHandle && dryerModel && dryerSerial) {
        this.selectedModel = dryerModel;
        this.selectedSerial = dryerSerial;
        // Skip URL update when applying from URL (URL already has the params)
        await this.filterProductsByHandle(productHandle, true);
      }
    }

    async loadDryerKitData() {
      try {
        const store = await getDryerKitMetaobjectsStore();
        this.serialMap = store.serialMap || {};
        this.productMap = store.productMap || {};
        this.finalizeData();
      } catch (error) {
        this.populateModelDropdown();
      }
    }

    async fetchSectionPage(root, sectionId, page, pageParam) {
      try {
        const param = pageParam || 'page';
        const url = `${root}?section_id=${sectionId}&${param}=${page}`;
        const response = await fetch(url);
        if (!response.ok) return null;

        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const jsonScript = doc.querySelector('#dryer-kit-json-data');

        if (jsonScript && jsonScript.textContent) {
          return JSON.parse(jsonScript.textContent);
        }
        return null;
      } catch (error) {
        return null;
      }
    }

    processDataString(dataString) {
      if (!dataString || typeof dataString !== 'string' || !dataString.trim()) return;

      if (!this.serialMap) this.serialMap = {};
      if (!this.productMap) this.productMap = {};

      const entries = dataString.split('##');
      entries.forEach(entry => {
        if (!entry || !entry.trim()) return;

        const parts = entry.split('|');
        if (parts.length < 2) return;

        const hasId = parts.length >= 4;
        const model = (hasId ? parts[1] : parts[0]).trim();
        const serial = (hasId ? parts[2] : parts[1]).trim();
        const productHandle = (hasId && parts.length >= 4 ? parts[3] : (parts.length >= 3 ? parts[2] : '')).trim();

        if (!model || !serial) return;

        if (!this.serialMap[model]) {
          this.serialMap[model] = [];
        }

        const exists = this.serialMap[model].some(s => {
          const existingSerial = typeof s === 'string' ? s : s.serial;
          return existingSerial === serial;
        });

        if (!exists) {
          this.serialMap[model].push({ serial, productHandle });
        }

        const mapKey = `${model}|${serial}`;
        if (productHandle) {
          this.productMap[mapKey] = productHandle;
        }
      });
    }

    finalizeData() {
      // Sort serial numbers for each model
      Object.keys(this.serialMap).forEach(model => {
        this.serialMap[model].sort((a, b) => {
          const serialA = typeof a === 'string' ? a : a.serial;
          const serialB = typeof b === 'string' ? b : b.serial;
          return serialA.localeCompare(serialB, undefined, { numeric: true, sensitivity: 'base' });
        });
      });

      // Populate model dropdown from loaded data
      this.populateModelDropdown();
      // Refresh custom-select so dynamically added options have click handlers (fixes first-load dropdown)
      const refreshModelSelect = () => {
        if (this.modelSelect && typeof this.modelSelect.refreshOptions === 'function') {
          this.modelSelect.refreshOptions();
        }
      };
      refreshModelSelect();
      if (typeof this.modelSelect?.refreshOptions !== 'function') {
        requestAnimationFrame(refreshModelSelect);
      }
    }

    populateModelDropdown() {
      if (!this.modelList) return;

      const models = Object.keys(this.serialMap).sort((a, b) =>
        a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
      );

      // Remove loading placeholder
      const loadingItem = this.modelList.querySelector('.dryer-models-loading');
      if (loadingItem) loadingItem.remove();

      // Remove existing model options (keep default "Select Dryer Model")
      const existingOptions = this.modelList.querySelectorAll('li[data-value]:not([data-value=""])');
      existingOptions.forEach(opt => opt.remove());

      if (models.length === 0) {
        const noModelsLi = document.createElement('li');
        noModelsLi.className = 'form-check';
        noModelsLi.setAttribute('role', 'menuitem');
        noModelsLi.innerHTML = '<span class="option_txt form-check-label">No models found</span>';
        this.modelList.appendChild(noModelsLi);
        return;
      }

      models.forEach((model, index) => {
        const li = document.createElement('li');
        li.setAttribute('data-value', model);
        li.className = 'form-check';
        li.setAttribute('role', 'menuitem');

        const optionIndex = index + 1;
        const inputId = `dryer-model-option${optionIndex}`;
        const escapedModel = model.replace(/"/g, '&quot;');

        li.innerHTML = `<input type="radio" value="${escapedModel}" class="sortby_options form-check-input" name="dryer-model" id="${inputId}" tabindex="-1"><label for="${inputId}" class="option_txt form-check-label" title="${escapedModel}">${model}</label>`;
        this.modelList.appendChild(li);
      });

      // Re-bind click/change events on the new model list items
      this.rebindModelListEvents();
    }

    rebindModelListEvents() {
      if (!this.modelList) return;

      this.modelList.querySelectorAll('li[data-value]').forEach(item => {
        item.addEventListener('click', (e) => {
          e.stopPropagation();
          const li = e.target.closest('li[data-value]');
          if (!li) return;
          const radio = li.querySelector('input[type="radio"]');
          if (radio) {
            radio.checked = true;
            this.modelList.querySelectorAll('li').forEach(el => el.classList.remove('selected'));
            li.classList.add('selected');
            radio.dispatchEvent(new Event('change', { bubbles: true }));

            // Close the model dropdown after selection
            if (this.modelSelect && typeof this.modelSelect.closeDropdown === 'function') {
              this.modelSelect.closeDropdown();
            }
          }
        });
      });
    }

    processData(data) {
      if (!data || !data.data) return;
      this.serialMap = {};
      this.productMap = {};
      this.processDataString(data.data);
      this.finalizeData();
    }

    bindEvents() {
      // Disable serial number dropdown initially
      if (this.serialToggle) {
        this.serialToggle.disabled = true;
        this.serialToggle.setAttribute('aria-disabled', 'true');
      }

      // Listen for model selection - multiple event types for custom-select component
      if (this.modelList) {
        // Listen for change events on radio inputs
        this.modelList.addEventListener('change', (e) => {
          if (e.target.type === 'radio' && e.target.name === 'dryer-model') {
            const value = e.target.value;
            if (value === '') {
              // Default "Select Dryer Model" option selected
              this.selectedModel = null;
              const modelPlaceholder = this.modelToggle?.querySelector('.option_txt');
              if (modelPlaceholder) {
                modelPlaceholder.textContent = 'Select Dryer Model';
              }
              // Disable and reset serial dropdown
              if (this.serialToggle) {
                this.serialToggle.disabled = true;
                this.serialToggle.setAttribute('aria-disabled', 'true');
              }
              if (this.serialList) {
                this.serialList.innerHTML = '<li class="form-check selected" role="menuitem" tabindex="0" data-value=""><input type="radio" value="" class="sortby_options form-check-input" name="dryer-serial" id="serial-number-option-default" tabindex="-1" checked selected><label for="serial-number-option-default" class="option_txt form-check-label" title="Select your Serial Number">Select your Serial Number</label></li>';
              }
              const serialPlaceholder = this.serialToggle?.querySelector('.option_txt');
              if (serialPlaceholder) {
                serialPlaceholder.textContent = 'Select Serial Number';
              }
              this.selectedSerial = null;
              return;
            }
            this.onModelSelect(value);
          }
        });

        // Listen for clicks on list items, labels, and inputs
        this.modelList.addEventListener('click', (e) => {
          // Check if clicked element is a label, input, or li
          let li = null;
          if (e.target.tagName === 'LABEL') {
            li = e.target.closest('li[data-value]');
          } else if (e.target.tagName === 'INPUT') {
            li = e.target.closest('li[data-value]');
          } else if (e.target.tagName === 'LI') {
            li = e.target;
          } else {
            li = e.target.closest('li[data-value]');
          }

          if (li) {
            const radio = li.querySelector('input[type="radio"][name="dryer-model"]');
            if (radio) {
              radio.checked = true;
              // Update selected class
              this.modelList.querySelectorAll('li').forEach(item => item.classList.remove('selected'));
              li.classList.add('selected');
              // Trigger change event
              radio.dispatchEvent(new Event('change', { bubbles: true }));
              if (radio.value === '') {
                return; // Change event handler will handle it
              }
              this.onModelSelect(radio.value);
            }
          }
        });

        // Also listen for input events
        this.modelList.addEventListener('input', (e) => {
          if (e.target.type === 'radio' && e.target.name === 'dryer-model') {
            const value = e.target.value;
            if (value === '') {
              return; // Change event handler will handle default option
            }
            this.onModelSelect(value);
          }
        });
      }

      // Watch for changes in the model toggle button text (when custom-select updates it)
      if (this.modelToggle) {
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.type === 'childList' || mutation.type === 'characterData') {
              const optionText = this.modelToggle.querySelector('.option_txt');
              if (optionText && optionText.textContent && 
                  optionText.textContent !== 'Select Model' && 
                  optionText.textContent !== 'Select Dryer Model') {
                // Find the model value from the list
                const modelLi = this.modelList?.querySelector(`li[data-value="${optionText.textContent.trim()}"]`);
                if (modelLi) {
                  const radio = modelLi.querySelector('input[type="radio"]');
                  if (radio && radio.value && radio.value !== '') {
                    const currentModel = this.selectedModel;
                    if (currentModel !== radio.value) {
                      this.onModelSelect(radio.value);
                    }
                  }
                }
              }
            }
          });
        });

        const optionText = this.modelToggle.querySelector('.option_txt');
        if (optionText) {
          observer.observe(optionText, { 
            childList: true, 
            characterData: true, 
            subtree: true 
          });
        }
      }
    }

    onModelSelect(model) {
      if (!model || model === '') {
        return;
      }

      // Decode HTML entities that might be in the model value
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = model;
      const decodedModel = tempDiv.textContent || tempDiv.innerText || model;
      const trimmedModel = decodedModel.trim();

      this.selectedModel = trimmedModel;

      // Update selected class on model list items
      if (this.modelList) {
        this.modelList.querySelectorAll('li').forEach(item => {
          item.classList.remove('selected');
          const radio = item.querySelector('input[type="radio"]');
          if (radio && (radio.value === model || radio.value === decodedModel || radio.value === trimmedModel)) {
            item.classList.add('selected');
            radio.checked = true;
          }
        });
      }

      // Update model button text
      const modelPlaceholder = this.modelToggle?.querySelector('.option_txt');
      if (modelPlaceholder) {
        modelPlaceholder.textContent = trimmedModel;
      }

      // Close model dropdown
      if (this.modelToggle) {
        this.modelToggle.setAttribute('aria-expanded', 'false');
        const modelDropdown = document.querySelector('#dryer-model-dropdown');
        if (modelDropdown) {
          modelDropdown.setAttribute('aria-hidden', 'true');
        }
      }

      // Enable serial number dropdown
      if (this.serialToggle) {
        this.serialToggle.disabled = false;
        this.serialToggle.removeAttribute('aria-disabled');
      }

      // Populate serial numbers - try with trimmed/decoded model
      this.populateSerials(trimmedModel);
    }

    findMatchingModelKey(selectedModel) {
      // Try multiple matching strategies
      const normalized = selectedModel.trim();
      const normalizedLower = normalized.toLowerCase();
      
      // 1. Exact match
      if (this.serialMap[selectedModel]) {
        return selectedModel;
      }
      
      // 2. Trimmed exact match
      const trimmedMatch = Object.keys(this.serialMap).find(key => key.trim() === normalized);
      if (trimmedMatch) {
        return trimmedMatch;
      }
      
      // 3. Case-insensitive match
      const caseInsensitiveMatch = Object.keys(this.serialMap).find(key => 
        key.trim().toLowerCase() === normalizedLower
      );
      if (caseInsensitiveMatch) {
        return caseInsensitiveMatch;
      }
      
      // 4. Partial match (contains)
      const partialMatch = Object.keys(this.serialMap).find(key => 
        key.trim().toLowerCase().includes(normalizedLower) || 
        normalizedLower.includes(key.trim().toLowerCase())
      );
      if (partialMatch) {
        return partialMatch;
      }
      
      return null;
    }

    populateSerials(model) {
      if (!this.serialList) {
        return;
      }

      this.serialList.innerHTML = '';

      // Find the matching model key
      const matchingKey = this.findMatchingModelKey(model);
      
      if (!matchingKey) {
        const noSerialsMsg = window.collectionSearchMessages?.dryerNoSerials || 'No serial numbers found for this model';
        this.serialList.innerHTML = `<li class="form-check"><span class="option_txt form-check-label">${noSerialsMsg}</span></li>`;
        
        const serialPlaceholder = this.serialToggle?.querySelector('.option_txt');
        if (serialPlaceholder) {
          serialPlaceholder.textContent = 'No serial numbers';
        }
        return;
      }

      const serials = this.serialMap[matchingKey] || [];

      if (serials.length === 0) {
        this.serialList.innerHTML = '';
        
        // Add default option
        const defaultLi = document.createElement('li');
        defaultLi.className = 'form-check selected';
        defaultLi.setAttribute('role', 'menuitem');
        defaultLi.setAttribute('tabindex', '0');
        defaultLi.setAttribute('data-value', '');

        const defaultInput = document.createElement('input');
        defaultInput.type = 'radio';
        defaultInput.value = '';
        defaultInput.className = 'sortby_options form-check-input';
        defaultInput.name = 'dryer-serial';
        defaultInput.id = 'serial-number-option-default';
        defaultInput.setAttribute('tabindex', '-1');
        defaultInput.checked = true;

        const defaultLabel = document.createElement('label');
        defaultLabel.htmlFor = defaultInput.id;
        defaultLabel.className = 'option_txt form-check-label';
        const noSerialsMsg = window.collectionSearchMessages?.dryerNoSerials || 'No serial numbers found for this model';
        defaultLabel.textContent = noSerialsMsg;
        defaultLabel.setAttribute('title', noSerialsMsg);

        defaultLi.appendChild(defaultInput);
        defaultLi.appendChild(defaultLabel);
        this.serialList.appendChild(defaultLi);
        
        // Update serial button text
        const serialPlaceholder = this.serialToggle?.querySelector('.option_txt');
        if (serialPlaceholder) {
          serialPlaceholder.textContent = 'No serial numbers';
        }
        return;
      }

      // Remove duplicates and sort
      const uniqueSerials = [];
      const seen = new Set();
      
      serials.forEach(item => {
        const serial = typeof item === 'string' ? item : item.serial;
        if (serial && !seen.has(serial)) {
          seen.add(serial);
          uniqueSerials.push(serial);
        }
      });

      uniqueSerials.sort((a, b) => 
        a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
      );

      // Add default "Select your Serial Number" option as first item (selected by default)
      const defaultLi = document.createElement('li');
      defaultLi.className = 'form-check selected';
      defaultLi.setAttribute('role', 'menuitem');
      defaultLi.setAttribute('tabindex', '0');
      defaultLi.setAttribute('data-value', '');

      const defaultInput = document.createElement('input');
      defaultInput.type = 'radio';
      defaultInput.value = '';
      defaultInput.className = 'sortby_options form-check-input';
      defaultInput.name = 'dryer-serial';
      defaultInput.id = 'serial-number-option-default';
      defaultInput.setAttribute('tabindex', '-1');
      defaultInput.checked = true;
      defaultInput.setAttribute('selected', '');

      const defaultLabel = document.createElement('label');
      defaultLabel.htmlFor = defaultInput.id;
      defaultLabel.className = 'option_txt form-check-label';
      defaultLabel.textContent = 'Select your Serial Number';
      defaultLabel.setAttribute('title', 'Select your Serial Number');

      defaultLi.appendChild(defaultInput);
      defaultLi.appendChild(defaultLabel);
      
      // Add click handler for default option
      defaultLi.addEventListener('click', (e) => {
        e.stopPropagation();
        defaultInput.checked = true;
        this.serialList.querySelectorAll('li').forEach(item => item.classList.remove('selected'));
        defaultLi.classList.add('selected');
        this.selectedSerial = null; // Clear selection when default is chosen
        
        // Update button text
        const serialPlaceholder = this.serialToggle?.querySelector('.option_txt');
        if (serialPlaceholder) {
          serialPlaceholder.textContent = 'Select your Serial Number';
        }
      });
      
      this.serialList.appendChild(defaultLi);
      
      // Reset selectedSerial when default option is present
      this.selectedSerial = null;

      // Populate serial number list
      uniqueSerials.forEach((serial, index) => {
        const li = document.createElement('li');
        li.className = 'form-check';
        li.setAttribute('role', 'menuitem');
        li.setAttribute('tabindex', '0');
        li.setAttribute('data-value', serial);

        const input = document.createElement('input');
        input.type = 'radio';
        input.value = serial;
        input.className = 'sortby_options form-check-input';
        input.name = 'dryer-serial';
        input.id = `serial-number-option${index + 2}`;
        input.setAttribute('tabindex', '-1');

        const label = document.createElement('label');
        label.htmlFor = input.id;
        label.className = 'option_txt form-check-label';
        label.textContent = serial;
        label.setAttribute('title', serial);

        li.appendChild(input);
        li.appendChild(label);
        this.serialList.appendChild(li);

        // Add click handler - work with custom-select component
        li.addEventListener('click', (e) => {
          e.stopPropagation(); // Prevent bubbling to body click handler
          input.checked = true;
          
          // Update the selected class on the list item (for custom-select styling)
          this.serialList.querySelectorAll('li').forEach(item => item.classList.remove('selected'));
          li.classList.add('selected');
          
          // Update button text immediately
          const serialPlaceholder = this.serialToggle?.querySelector('.option_txt');
          if (serialPlaceholder) {
            serialPlaceholder.textContent = serial;
          }
          
          // Store selected serial and close the dropdown
          this.selectedSerial = serial;
          this.onSerialSelect(serial);
        });
      });

      // Refresh custom-select so dynamically added serial options have click handlers
      if (this.serialSelect && typeof this.serialSelect.refreshOptions === 'function') {
        this.serialSelect.refreshOptions();
      }

      // Update serial button text to default option
      const serialPlaceholder = this.serialToggle?.querySelector('.option_txt');
      if (serialPlaceholder) {
        serialPlaceholder.textContent = 'Select your Serial Number';
      }
      
      // Ensure default option is selected visually
      const defaultOption = this.serialList.querySelector('[data-value=""]');
      if (defaultOption) {
        defaultOption.classList.add('selected');
        const defaultRadio = defaultOption.querySelector('input[type="radio"]');
        if (defaultRadio) {
          defaultRadio.checked = true;
        }
      }
    }

    onSerialSelect(serial) {
      this.selectedSerial = serial;
      
      // Close serial dropdown using custom-select component method
      if (this.serialSelect) {
        // Check if it's a custom-select element with closeDropdown method
        if (this.serialSelect.closeDropdown && typeof this.serialSelect.closeDropdown === 'function') {
          this.serialSelect.closeDropdown();
        } else {
          // Fallback: manually close using Utility
          if (window.Utility && typeof window.Utility.toggleElement === 'function') {
            window.Utility.toggleElement(this.serialSelect, 'close');
          }
          
          // Ensure all dropdown states are closed
          if (this.serialToggle) {
            this.serialToggle.setAttribute('aria-expanded', 'false');
          }
          
          const serialDropdown = document.querySelector('#serial-number-dropdown');
          if (serialDropdown) {
            serialDropdown.setAttribute('aria-hidden', 'true');
            serialDropdown.classList.remove('open');
          }
          
          // Close the custom-select wrapper
          if (this.serialSelect) {
            this.serialSelect.classList.remove('open');
          }
        }
      }
    }

    bindSubmitEvent() {
      if (!this.submitBtn) {
        return;
      }

      this.submitBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleSubmit();
      });
    }
    
    bindClearEvent() {
      if (!this.clearBtn) {
        return;
      }

      this.clearBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.clearFilter();
      });
    }

    async handleSubmit() {
      if (!this.selectedModel || !this.selectedSerial || this.selectedSerial === '') {
        Utility.handleError('Please select both a model and serial number.', 'Validation Error');
        // Forcefully click clear button to reset filter
        if (this.clearBtn) {
          this.clearBtn.click();
        } else {
          this.clearFilter();
        }
        return;
      }
      
      // CRITICAL: Reset "no product shown" tracking for new search from submit button
      // This allows a new search even if previous search showed "no product"
      this.noProductShownForParams = null;

      // Show loader immediately when submit is clicked
      this.showSubmitLoader();

      // CRITICAL: Product handle MUST come from metaobject data
      // This ensures only products linked via dryer_kit metaobject are displayed

      // Normalize model and serial for lookup
      const normalizedModel = this.selectedModel.trim();
      const normalizedSerial = this.selectedSerial.trim();
      
      // Try multiple key variations
      const keyVariations = [
        `${normalizedModel}|${normalizedSerial}`,
        `${this.selectedModel}|${this.selectedSerial}`,
        `${normalizedModel}|${this.selectedSerial}`,
        `${this.selectedModel}|${normalizedSerial}`
      ];

      let productHandle = null;
      
      // Try exact matches first
      for (const key of keyVariations) {
        if (this.productMap[key]) {
          productHandle = this.productMap[key];
          break;
        }
      }

      // If not found with exact match, try to find it in serialMap with fuzzy matching
      if (!productHandle) {
        const matchingKey = this.findMatchingModelKey(normalizedModel);
        
        if (matchingKey && this.serialMap[matchingKey]) {
          const serialData = this.serialMap[matchingKey].find(s => {
            const serial = typeof s === 'string' ? s : s.serial;
            const serialTrimmed = serial ? serial.trim() : '';
            return serial === normalizedSerial || 
                   serial === this.selectedSerial ||
                   serialTrimmed === normalizedSerial ||
                   serialTrimmed === this.selectedSerial.trim();
          });
          
          if (serialData && serialData.productHandle) {
            productHandle = serialData.productHandle;
          }
        }
      }

      // If still not found, try case-insensitive and partial matching on productMap keys
      if (!productHandle) {
        const searchModelLower = normalizedModel.toLowerCase();
        const searchSerialLower = normalizedSerial.toLowerCase();
        
        for (const [key, handle] of Object.entries(this.productMap)) {
          const [keyModel, keySerial] = key.split('|');
          if (keyModel && keySerial) {
            const keyModelLower = keyModel.trim().toLowerCase();
            const keySerialLower = keySerial.trim().toLowerCase();
            
            if ((keyModelLower === searchModelLower || 
                 keyModelLower.includes(searchModelLower) || 
                 searchModelLower.includes(keyModelLower)) &&
                (keySerialLower === searchSerialLower ||
                 keySerialLower.includes(searchSerialLower) ||
                 searchSerialLower.includes(keySerialLower))) {
              productHandle = handle;
              break;
            }
          }
        }
      }

      if (!productHandle) {
        this.hideSubmitLoader();
        Utility.handleError('No product found for the selected model and serial number.', 'Product Not Found');
        this.clearFilter();
        return;
      }

      // Verify product handle was found from metaobject
      if (!productHandle || productHandle.trim() === '') {
        this.hideSubmitLoader();
        Utility.handleError('No product found in metaobject for the selected model and serial number.', 'Product Not Found');
        this.clearFilter();
        return;
      }
      
      // Filter to show ONLY this product from metaobject
      // Loader will be hidden in finishFiltering or showNoProductMessage
      await this.filterProductsByHandle(productHandle);
    }

    async filterProductsByHandle(productHandle, skipUrlUpdate = false) {
      // CRITICAL: Prevent concurrent searches - only one search at a time
      if (this.isSearching) {
        // console.log('[DryerKitFilter] filterProductsByHandle: Already searching, skipping duplicate call');
        return;
      }
      
      // CRITICAL: Lock search immediately
      this.isSearching = true;
      // console.log('[DryerKitFilter] filterProductsByHandle: Search locked');
      
      // CRITICAL: This function displays ONLY the product from metaobject
      // The productHandle parameter comes from dryer_kit metaobject's element_kit field
      // Searches across ALL pages of the collection to find the matching product
      // skipUrlUpdate: if true, URL won't be updated (used when re-applying from URL)
      
      // console.log('[DryerKitFilter] filterProductsByHandle called with:', productHandle, 'skipUrlUpdate:', skipUrlUpdate);
      
      const productsListing = document.querySelector('#products-listing');
      if (!productsListing) {
        console.warn('[DryerKitFilter] products-listing not found!');
        this.isSearching = false; // Unlock
        return;
      }

      // Validate product handle
      if (!productHandle || productHandle.trim() === '') {
        Utility.handleError('Invalid product handle. Product must come from metaobject.', 'Validation Error');
        this.isSearching = false; // Unlock
        this.clearFilter();
        return;
      }

      const normalizedHandle = productHandle.trim();
      
      // CRITICAL: Reset flags and increment unique ID at the start of each new search
      this.isShowingLoadingMessage = false;
      this.isShowingNoMatchMessage = false;
      this.messageUniqueId++; // Increment unique ID for this search
      const currentSearchId = this.messageUniqueId; // Store current search ID
      this.currentSearchId = currentSearchId; // Store for message creation
      
      // CRITICAL: Clean up ALL messages using the single container method
      this.hideMessageContainer();
      
      // console.log('[DryerKitFilter] filterProductsByHandle: Cleaned up all messages, starting search ID:', currentSearchId);
      
      // Hide products at start of search
      if (productsListing) {
        
        // Hide all products immediately when search starts
        // This prevents products from being displayed while searching
        const allCards = productsListing.querySelectorAll('[data-product-card]');
        allCards.forEach(card => {
          card.style.display = 'none';
          card.classList.add('dryer-kit-hidden');
        });
        
        // Hide promo blocks
        const promoBlocks = productsListing.querySelectorAll('.card-product-promo');
        promoBlocks.forEach(promoBlock => {
          promoBlock.style.display = 'none';
          promoBlock.classList.add('dryer-kit-hidden');
        });
      }
      
      // First, check current page
      const foundOnCurrentPage = this.filterCurrentPage(normalizedHandle);
      
      if (foundOnCurrentPage) {
        // Product found on current page - we're done
        // Hide message container
        this.hideMessageContainer();
        this.finishFiltering(1, productHandle, skipUrlUpdate);
        // Note: finishFiltering will unlock isSearching
        return;
      }
      
      // Product not found on current page - search all pages
      // console.log('[DryerKitFilter] Product not found on current page, searching all pages...');
      this.showLoadingMessage();
      
      // Double-check current page one more time (in case it was added after initial check)
      if (productsListing) {
        const allCards = productsListing.querySelectorAll('[data-product-card]');
        const existingCard = Array.from(allCards).find(card => {
          const cardHandle = (card.getAttribute('data-product-handle') || '').trim();
          return cardHandle === normalizedHandle && !card.classList.contains('dryer-kit-hidden');
        });
        
        if (existingCard) {
          // Product exists and is visible - just filter current page
          // console.log('[DryerKitFilter] Product found in grid on second check');
          this.filterCurrentPage(normalizedHandle);
          this.finishFiltering(1, productHandle, skipUrlUpdate);
          return;
        }
      }
      
      // Load all products from all pages (duplicates are already prevented in loadAllProducts)
      await this.loadAllProducts();
      
      // Search through all loaded products (find first match)
      const matchingProduct = this.allProducts.find(product => {
        const handle = (product.handle || '').trim();
        return handle === normalizedHandle;
      });
      
      if (matchingProduct) {
        // Found in the current collection pages
        this.displayProductFromAllPages(matchingProduct);
        this.finishFiltering(1, productHandle, skipUrlUpdate);
      } else {
        // Not in current collection — search across the whole store
        console.log('[DryerKitFilter] Product not in current collection, searching store-wide...');
        const storeProduct = await this.searchProductAcrossStore(normalizedHandle);

        if (storeProduct) {
          console.log('[DryerKitFilter] Found product via store-wide search:', storeProduct.handle);
          this.displayProductFromAllPages(storeProduct);
          this.finishFiltering(1, productHandle, skipUrlUpdate);
        } else {
          console.warn('[DryerKitFilter] Product not found anywhere in the store:', normalizedHandle);
          this.allProducts       = [];
          this.isLoadingProducts = false;
          this.hideSubmitLoader();
          this.showNoProductMessage();
          this.finishFiltering(0, productHandle, skipUrlUpdate);
        }
      }
    }

    async searchProductAcrossStore(productHandle) {
      const parser = new DOMParser();
      const root   = window.Shopify?.routes?.root || '/';

      // Helper: scan a fetched HTML document for a card matching productHandle
      const findCard = (doc) => {
        const cards = doc.querySelectorAll('[data-product-card]');
        for (const card of cards) {
          const handle = (card.getAttribute('data-product-handle') || '').trim();
          if (handle === productHandle) {
            // Keep the column wrapper so the card renders correctly in the grid
            const wrapper = card.closest('[class*="col-"]') || card.closest('[data-cross-reference-product-wrapper]');
            return { handle, html: wrapper ? wrapper.outerHTML : card.outerHTML };
          }
        }
        return null;
      };

      // 1. Try the store search endpoint
      try {
        const searchUrl = `${root}search?q=${encodeURIComponent(productHandle)}&type=product`;
        const res = await fetch(searchUrl);
        if (res.ok) {
          const found = findCard(parser.parseFromString(await res.text(), 'text/html'));
          if (found) return found;
        }
      } catch (e) { /* try next */ }

      // 2. Try /collections/all which contains every product in the store
      try {
        const allUrl = `${root}collections/all`;
        const res = await fetch(allUrl);
        if (res.ok) {
          const found = findCard(parser.parseFromString(await res.text(), 'text/html'));
          if (found) return found;
        }
      } catch (e) { /* try next */ }

      // 3. Try fetching the product page directly and build a minimal card from it
      try {
        const productUrl = `${root}products/${productHandle}`;
        const res = await fetch(productUrl);
        if (res.ok) {
          const doc   = parser.parseFromString(await res.text(), 'text/html');
          // Check if it's a real product page (not a 404)
          const title = doc.querySelector('[data-product-title], h1.product__title, .product-single__title');
          if (title) {
            // Build a minimal product card using data from the product page
            const imageEl  = doc.querySelector('.product__media img, .product-single__photo img');
            const imageSrc = imageEl ? (imageEl.getAttribute('src') || imageEl.getAttribute('data-src') || '') : '';
            const titleText = title.textContent.trim();
            const minimalHtml = `
              <div class="col-lg-3 col-md-4 col-6 px-md-3 px-2 d-flex align-items-stretch justify-content-around">
                <div class="card card-product text-start" data-product-card data-product-handle="${productHandle}" data-title="${titleText.replace(/"/g, '&quot;')}">
                  ${imageSrc ? `<img src="${imageSrc}" alt="${titleText.replace(/"/g, '&quot;')}" class="img-fluid">` : ''}
                  <div class="card-body">
                    <h3 class="fs-xl fw-bold mb-0"><a href="${productUrl}">${titleText}</a></h3>
                  </div>
                </div>
              </div>`;
            return { handle: productHandle, html: minimalHtml };
          }
        }
      } catch (e) { /* exhausted all options */ }

      return null;
    }
    
    filterCurrentPage(normalizedHandle) {
      const productsListing = document.querySelector('#products-listing');
      if (!productsListing) return false;
      
      const allCards = productsListing.querySelectorAll('[data-product-card]');
      const promoBlocks = productsListing.querySelectorAll('.card-product-promo');
      let found = false;
      
      // Filter products on current page
      allCards.forEach((card) => {
        // Hide promo blocks when filter is active
        if (card.classList.contains('card-product-promo')) {
          card.style.display = 'none';
          card.classList.add('dryer-kit-hidden');
          return;
        }
        
        // Process regular product cards
        const cardHandle = card.getAttribute('data-product-handle') || '';
        const cardHandleNormalized = cardHandle.trim();
        
        if (cardHandleNormalized === normalizedHandle) {
          // This is the matching product - show it
          card.style.display = '';
          card.classList.remove('dryer-kit-hidden');
          found = true;
          // console.log(`[DryerKitFilter] Found matching product on current page: ${cardHandle}`);
        } else {
          // This product doesn't match - hide it
          card.style.display = 'none';
          card.classList.add('dryer-kit-hidden');
        }
      });
      
      // Hide promo blocks
      promoBlocks.forEach((promoBlock) => {
        promoBlock.style.display = 'none';
        promoBlock.classList.add('dryer-kit-hidden');
      });
      
      return found;
    }
    
    showSubmitLoader() {
      if (!this.submitBtn) return;
      
      // Disable button and show loader
      this.submitBtn.disabled = true;
      this.submitBtn.style.opacity = '0.6';
      this.submitBtn.style.cursor = 'not-allowed';
      
      // Add loader spinner to button
      if (!this.submitBtn.querySelector('.dryer-kit-submit-loader')) {
        const loader = document.createElement('span');
        loader.className = 'dryer-kit-submit-loader';
        loader.innerHTML = ' <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>';
        loader.style.marginLeft = '8px';
        this.submitBtn.appendChild(loader);
      }
    }
    
    hideSubmitLoader() {
      if (!this.submitBtn) return;
      
      // Re-enable button and hide loader
      this.submitBtn.disabled = false;
      this.submitBtn.style.opacity = '';
      this.submitBtn.style.cursor = '';
      
      // Remove loader spinner from button
      const loader = this.submitBtn.querySelector('.dryer-kit-submit-loader');
      if (loader) {
        loader.remove();
      }
    }
    
    showLoadingMessage() {
      // Set flags
      this.isShowingLoadingMessage = true;
      this.isShowingNoMatchMessage = false;
      
      const productsListing = document.querySelector('#products-listing');
      if (!productsListing) return;
      
      // Hide all current products
      const allCards = productsListing.querySelectorAll('[data-product-card]');
      allCards.forEach(card => {
        card.style.display = 'none';
        card.classList.add('dryer-kit-hidden');
      });
      
      // Remove any legacy messages
      document.querySelectorAll('.dryer-kit-loading-message, .dryer-kit-no-match-message').forEach(msg => msg.remove());
      
      // Get or create the single container
      const container = this.getMessageContainer();
      if (!container) return;
      
      // Update content and show
      const loaderSrText = window.collectionSearchMessages?.searchLoaderSrText || 'Loading...';
      const searchingHeading = window.collectionSearchMessages?.dryerSearchingHeading || 'Searching all pages...';
      const searchingSubtext = (window.collectionSearchMessages?.dryerSearchingSubtext || 'Looking for product matching Model: [model], Serial: [serial]')
        .replace('[model]', this.selectedModel)
        .replace('[serial]', this.selectedSerial);
      const searchingDescription = window.collectionSearchMessages?.dryerSearchingDescription || 'Please wait while we search through all pages.';
      
      container.innerHTML = `
        <div class="d-flex flex-column align-items-center justify-content-center">
          <div class="spinner-border text-primary mb-3" role="status" style="width: 3rem; height: 3rem;">
            <span class="visually-hidden">${loaderSrText}</span>
          </div>
          <div class="mb-4 h4">${searchingHeading}</div>
          <div class="mb-3">${searchingSubtext}</div>
          <div class="mb-3">${searchingDescription}</div>
        </div>
      `;
      container.style.display = 'block';
      
      // console.log('[DryerKitFilter] showLoadingMessage: Displayed');
    }
    
    displayProductFromAllPages(product) {
      const productsListing = document.querySelector('#products-listing');
      if (!productsListing) return;
      
      // Reset flags when product is found
      this.isShowingLoadingMessage = false;
      this.isShowingNoMatchMessage = false;
      
      // Hide message container and remove legacy messages
      this.hideMessageContainer();
      
      const productGrid = productsListing.querySelector('#product-grids, .product-list__grid, [data-products-grid]');
      if (!productGrid) return;
      
      const normalizedHandle = product.handle.trim();
      
      // Get all cards and find existing ones with this handle
      const allCards = Array.from(productsListing.querySelectorAll('[data-product-card]'));
      const existingCards = allCards.filter(card => {
        const cardHandle = (card.getAttribute('data-product-handle') || '').trim();
        return cardHandle === normalizedHandle;
      });
      
      // Remove any duplicate cards (keep only the first one)
      if (existingCards.length > 1) {
        // console.log(`[DryerKitFilter] Found ${existingCards.length} duplicate cards, removing extras`);
        for (let i = 1; i < existingCards.length; i++) {
          existingCards[i].remove();
        }
      }
      
      const existingCard = existingCards.length > 0 ? existingCards[0] : null;
      
      if (existingCard) {
        // Product already exists - just show it and hide others
        // console.log('[DryerKitFilter] Product already exists in grid, showing it');
        
        // Hide all other products
        allCards.forEach(card => {
          if (card === existingCard) {
            card.style.display = '';
            card.classList.remove('dryer-kit-hidden');
          } else {
            card.style.display = 'none';
            card.classList.add('dryer-kit-hidden');
          }
        });
        
        // Hide promo blocks
        const promoBlocks = productsListing.querySelectorAll('.card-product-promo');
        promoBlocks.forEach(promoBlock => {
          promoBlock.style.display = 'none';
          promoBlock.classList.add('dryer-kit-hidden');
        });
        
        // Process images in the existing card
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.processImagesInProductCard(existingCard);
          });
        });
      } else {
        // Product doesn't exist - add it
        // console.log('[DryerKitFilter] Product not in grid, adding it');
        
        // Hide all current products
        allCards.forEach(card => {
          card.style.display = 'none';
          card.classList.add('dryer-kit-hidden');
        });
        
        // Hide promo blocks
        const promoBlocks = productsListing.querySelectorAll('.card-product-promo');
        promoBlocks.forEach(promoBlock => {
          promoBlock.style.display = 'none';
          promoBlock.classList.add('dryer-kit-hidden');
        });
        
        // Display the matching product
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = product.html;
        const productCard = tempDiv.querySelector('[data-product-card]');
        if (productCard) {
          productCard.style.display = '';
          productCard.classList.remove('dryer-kit-hidden');
          productGrid.appendChild(productCard);
          
          // Process images to ensure they load correctly
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              this.processImagesInProductCard(productCard);
            });
          });
        }
      }
    }
    
    processImagesInProductCard(card) {
      if (!card) return;
      
      // Process images in the product card
      const images = card.querySelectorAll('img');
      for (let i = 0; i < images.length; i++) {
        const img = images[i];
        
        // Convert lazy loading attributes
        const dataSrc = img.getAttribute('data-src');
        const dataSrcset = img.getAttribute('data-srcset');
        const dataSizes = img.getAttribute('data-sizes');
        
        if (dataSrc && !img.src) {
          img.src = dataSrc;
          img.removeAttribute('data-src');
        }
        
        if (dataSrcset && !img.srcset) {
          img.srcset = dataSrcset;
          img.removeAttribute('data-srcset');
        }
        
        if (dataSizes && !img.sizes) {
          img.sizes = dataSizes;
          img.removeAttribute('data-sizes');
        }
        
        // Remove lazy loading classes
        img.classList.remove('lozad', 'lazy', 'lazyload');
        img.loading = 'eager';
      }
      
      // Process picture elements
      const pictures = card.querySelectorAll('picture');
      for (let i = 0; i < pictures.length; i++) {
        const picture = pictures[i];
        const sources = picture.querySelectorAll('source[data-srcset]');
        
        for (let j = 0; j < sources.length; j++) {
          const source = sources[j];
          if (source.hasAttribute('data-srcset')) {
            source.srcset = source.getAttribute('data-srcset');
            source.removeAttribute('data-srcset');
          }
        }
        
        const pictureImg = picture.querySelector('img');
        if (pictureImg) {
          const dataSrc = pictureImg.getAttribute('data-src');
          const dataSrcset = pictureImg.getAttribute('data-srcset');
          
          if (dataSrc && !pictureImg.src) {
            pictureImg.src = dataSrc;
            pictureImg.removeAttribute('data-src');
          }
          
          if (dataSrcset && !pictureImg.srcset) {
            pictureImg.srcset = dataSrcset;
            pictureImg.removeAttribute('data-srcset');
          }
          
          pictureImg.classList.remove('lozad', 'lazy', 'lazyload');
          pictureImg.loading = 'eager';
        }
      }
    }
    
    // Get or create the single message container - ensures only ONE message element exists
    getMessageContainer() {
      const productsListing = document.querySelector('#products-listing');
      if (!productsListing) return null;
      
      const productGrid = productsListing.querySelector('#product-grids, .product-list__grid, [data-products-grid]');
      if (!productGrid) return null;
      
      // Look for existing container
      let container = productGrid.querySelector('.dryer-kit-message-container');
      
      if (!container) {
        // Create single container that will be reused
        container = document.createElement('div');
        container.className = 'col-12 text-center mt-5 dryer-kit-message-container';
        container.style.display = 'none';
        productGrid.appendChild(container);
      }
      
      return container;
    }
    
    // Hide the message container
    hideMessageContainer() {
      const container = this.getMessageContainer();
      if (container) {
        container.style.display = 'none';
        container.innerHTML = '';
      }
      // Also remove any legacy messages
      document.querySelectorAll('.dryer-kit-loading-message, .dryer-kit-no-match-message').forEach(msg => msg.remove());
    }
    
    showNoProductMessage() {
      // CRITICAL: Check if already showing - prevent duplicates
      if (this.isShowingNoMatchMessage) {
        // console.log('[DryerKitFilter] showNoProductMessage: Already showing, skipping');
        return;
      }
      
      // Reset loading message flag
      this.isShowingLoadingMessage = false;
      this.isShowingNoMatchMessage = true;
      
      // CRITICAL: Track that we showed "no product" for these params
      // This prevents re-showing if URL triggers reapply
      const searchKey = `${this.selectedModel}|${this.selectedSerial}`;
      this.noProductShownForParams = searchKey;
      // console.log('[DryerKitFilter] showNoProductMessage: Tracking no product shown for:', searchKey);
      
      const productsListing = document.querySelector('#products-listing');
      if (!productsListing) return;
      
      // Ensure all products are hidden when no results found
      const allCards = productsListing.querySelectorAll('[data-product-card]');
      allCards.forEach(card => {
        card.style.display = 'none';
        card.classList.add('dryer-kit-hidden');
      });
      
      // Hide promo blocks
      const promoBlocks = productsListing.querySelectorAll('.card-product-promo');
      promoBlocks.forEach(promoBlock => {
        promoBlock.style.display = 'none';
        promoBlock.classList.add('dryer-kit-hidden');
      });
      
      // Remove any legacy messages
      document.querySelectorAll('.dryer-kit-loading-message, .dryer-kit-no-match-message').forEach(msg => msg.remove());
      
      // Get or create the single container
      const container = this.getMessageContainer();
      if (!container) return;
      
      // Update content and show
      const noProductHeading = window.collectionSearchMessages?.dryerNoProductHeading || 'No product found';
      const noProductMessage = (window.collectionSearchMessages?.dryerNoProductMessage || 'No product matching Model: [model], Serial: [serial] was found in this collection.')
        .replace('[model]', this.selectedModel)
        .replace('[serial]', this.selectedSerial);
      const noProductSuggestion = window.collectionSearchMessages?.dryerNoProductSuggestion || 'We searched through all pages. Please check the model and serial number, or clear the filter.';
      
      container.innerHTML = `
        <div class="mb-4 h4">${noProductHeading}</div>
        <div class="mb-3">${noProductMessage}</div>
        <div class="mb-3">${noProductSuggestion}</div>
      `;
      container.style.display = 'block';
      
      // console.log('[DryerKitFilter] showNoProductMessage: Displayed');
    }
    
    finishFiltering(visibleCount, productHandle, skipUrlUpdate = false) {
      // Hide message container when product is found
      if (visibleCount > 0) {
        this.hideMessageContainer();
        this.hideSubmitLoader();
      }
      
      // Update product count (even if 0)
      this.updateTotalProductsCount(visibleCount);
      
      // Hide pagination when filter is active (even if no results)
      this.hidePagination();
      
      // Only update URL when submit button is clicked, not when re-applying from URL
      if (!skipUrlUpdate) {
        // Update URL to reflect the filter (without page reload)
        // This includes when no results found - URL should still reflect the search
        this.updateUrlWithFilter(productHandle);
      }
      
      // Show clear button (so user can clear the filter if no results found)
      if (this.clearBtn) {
        this.clearBtn.style.display = '';
      }
      
      // CRITICAL: Unlock search after finishing
      this.isSearching = false;
      // console.log('[DryerKitFilter] finishFiltering: Search unlocked');
    }
    
    async loadAllProducts() {
      if (this.isLoadingProducts) {
        // Already loading, wait for it to complete
        while (this.isLoadingProducts) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
        return;
      }
      
      this.isLoadingProducts = true;
      this.allProducts = [];
      const seenHandles = new Set(); // Track handles to prevent duplicates
      
      try {
        // Get current page products
        const currentPageProducts = this.extractProductsFromPage();
        this.productsPerPage = currentPageProducts.length || 24;
        
        // Add current page products (avoiding duplicates)
        currentPageProducts.forEach(product => {
          const handle = (product.handle || '').trim();
          if (handle && !seenHandles.has(handle)) {
            seenHandles.add(handle);
            this.allProducts.push(product);
          }
        });
        
        // console.log(`[DryerKitFilter] Loaded ${this.allProducts.length} unique products from current page`);
        
        // Detect total pages from pagination
        const paginationLinks = document.querySelectorAll('[data-pagelinks] a, .pagination a, [data-pagination] a');
        let maxPage = 1;
        
        paginationLinks.forEach(link => {
          const href = link.getAttribute('href') || '';
          const text = (link.textContent || '').trim();
          
          // Check URL for page parameter
          const pageMatch = href.match(/[?&]page=(\d+)/);
          if (pageMatch) {
            const pageNum = parseInt(pageMatch[1]);
            if (pageNum > maxPage) maxPage = pageNum;
          }
          
          // Also check if link text is a number
          const textNum = parseInt(text);
          if (!isNaN(textNum) && textNum > maxPage) {
            maxPage = textNum;
          }
        });
        
        // Check for "Load More" button
        const loadMoreBtn = document.querySelector('[data-loadmore]');
        if (loadMoreBtn) {
          maxPage = Math.max(maxPage, 10); // At least check 10 pages
        }
        
        // console.log(`[DryerKitFilter] Detected ${maxPage} total pages, loading all pages...`);
        
        // Load remaining pages
        for (let page = 2; page <= maxPage; page++) {
          try {
            const pageProducts = await this.fetchCollectionPage(page);
            if (pageProducts && pageProducts.length > 0) {
              // Add products avoiding duplicates
              pageProducts.forEach(product => {
                const handle = (product.handle || '').trim();
                if (handle && !seenHandles.has(handle)) {
                  seenHandles.add(handle);
                  this.allProducts.push(product);
                }
              });
              // console.log(`[DryerKitFilter] Loaded page ${page}: ${pageProducts.length} products, ${this.allProducts.length} unique total`);
            } else {
              // No more pages
              break;
            }
          } catch (error) {
            console.warn(`[DryerKitFilter] Error loading page ${page}:`, error);
            break;
          }
        }
        
        // console.log(`[DryerKitFilter] Finished loading all products. Total unique: ${this.allProducts.length}`);
      } catch (error) {
        console.error('[DryerKitFilter] Error loading products:', error);
      } finally {
        this.isLoadingProducts = false;
      }
    }
    
    extractProductsFromPage() {
      const productsListing = document.querySelector('#products-listing');
      if (!productsListing) return [];
      
      const productGrid = productsListing.querySelector('#product-grids, .product-list__grid, [data-products-grid]');
      if (!productGrid) return [];
      
      const cards = productGrid.querySelectorAll('[data-product-card]');
      const products = [];
      
      cards.forEach((card) => {
        // Skip promo blocks
        if (card.classList.contains('card-product-promo')) {
          return;
        }
        
        const handle = card.getAttribute('data-product-handle') || '';
        if (handle) {
          products.push({
            handle: handle.trim(),
            html: card.outerHTML
          });
        }
      });
      
      return products;
    }
    
    async fetchCollectionPage(page) {
      return new Promise((resolve, reject) => {
        const url = new URL(window.location.href);
        // Preserve dryer filter params if they exist
        const dryerModel = url.searchParams.get('dryer_model');
        const dryerSerial = url.searchParams.get('dryer_serial');
        const productHandle = url.searchParams.get('product_handle');
        
        // Create new URL for the page
        url.searchParams.set('page', page);
        // Remove dryer params from page URL (we don't want server-side filtering)
        url.searchParams.delete('dryer_model');
        url.searchParams.delete('dryer_serial');
        url.searchParams.delete('product_handle');
        
        fetch(url.toString())
          .then(response => response.text())
          .then(html => {
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            const productsListing = doc.querySelector('#products-listing');
            if (!productsListing) {
              resolve([]);
              return;
            }
            
            const productGrid = productsListing.querySelector('#product-grids, .product-list__grid, [data-products-grid]');
            if (!productGrid) {
              resolve([]);
              return;
            }
            
            const cards = productGrid.querySelectorAll('[data-product-card]');
            const products = [];
            
            cards.forEach((card) => {
              // Skip promo blocks
              if (card.classList.contains('card-product-promo')) {
                return;
              }
              
              const handle = card.getAttribute('data-product-handle') || '';
              if (handle) {
                products.push({
                  handle: handle.trim(),
                  html: card.outerHTML
                });
              }
            });
            
            resolve(products);
          })
          .catch(error => {
            console.warn(`[DryerKitFilter] Error fetching page ${page}:`, error);
            reject(error);
          });
      });
    }
    
    storeOriginalProductCount() {
      // Store the original product count from data-totalproducts
      const totalProductsElement = document.querySelector('[data-totalproducts] p, [data-totalproducts]');
      if (totalProductsElement) {
        const text = totalProductsElement.textContent.trim();
        const match = text.match(/^(\d+)/);
        if (match && match[1]) {
          this.originalProductCount = parseInt(match[1], 10);
          // console.log(`[DryerKitFilter] Stored original count: ${this.originalProductCount}`);
        }
      }
    }
    
    updateTotalProductsCount(count) {
      // Update all data-totalproducts elements
      const totalProductsElements = document.querySelectorAll('[data-totalproducts]');
      
      if (totalProductsElements.length === 0) {
        return;
      }
      
      // Get the translated "results found" text
      const resultsFoundText = this.getResultsFoundText();
      
      totalProductsElements.forEach(element => {
        const paragraph = element.querySelector('p');
        if (paragraph) {
          paragraph.textContent = `${count} ${resultsFoundText}`;
        } else {
          element.textContent = `${count} ${resultsFoundText}`;
        }
      });
      
      // console.log(`[DryerKitFilter] Updated total products count to: ${count}`);
    }
    
    getResultsFoundText() {
      // Try to get the translated text from existing element
      const existingElement = document.querySelector('[data-totalproducts] p, [data-totalproducts]');
      if (existingElement && existingElement.textContent) {
        const text = existingElement.textContent.trim();
        const match = text.match(/^\d+\s+(.+)$/);
        if (match && match[1]) {
          return match[1];
        }
      }
      
      // Fallback to default
      return 'results found';
    }
    
    hidePagination() {
      // Hide pagination when filter is active
      const pagination = document.querySelector('[data-pagination-parent]');
      if (pagination) {
        pagination.style.display = 'none';
      }
    }
    
    showPagination() {
      // Show pagination when filter is cleared
      const pagination = document.querySelector('[data-pagination-parent]');
      if (pagination) {
        pagination.style.display = '';
      }
    }
    
    updateUrlWithFilter(productHandle) {
      // CRITICAL: Set flag BEFORE updating URL to prevent re-search loop
      this.isUpdatingUrl = true;
      // console.log('[DryerKitFilter] updateUrlWithFilter: Setting isUpdatingUrl = true');
      
      // Update URL parameters without page reload
      const url = new URL(window.location.href);
      url.searchParams.set('dryer_model', this.selectedModel);
      url.searchParams.set('dryer_serial', this.selectedSerial);
      url.searchParams.set('product_handle', productHandle);
      // Remove page parameter to show filtered results from page 1
      url.searchParams.delete('page');
      
      const newUrl = url.toString();
      console.log('[DryerKitFilter] Updating URL with filter:', { 
        model: this.selectedModel, 
        serial: this.selectedSerial, 
        handle: productHandle,
        newUrl 
      });
      
      window.history.pushState({}, '', newUrl);
      
      // CRITICAL: Reset flag after a delay (longer than any event handler delay)
      // The pushState handler has 100ms + 400ms = 500ms delay, so wait 700ms
      setTimeout(() => {
        this.isUpdatingUrl = false;
        // console.log('[DryerKitFilter] updateUrlWithFilter: Setting isUpdatingUrl = false');
      }, 700);
    }
    
    clearFilter() {
      // Hide submit loader if it's showing
      this.hideSubmitLoader();
      
      // CRITICAL: Unlock search when clearing filter
      this.isSearching = false;
      
      // Reset message flags
      this.isShowingLoadingMessage = false;
      this.isShowingNoMatchMessage = false;
      
      // CRITICAL: Reset tracking flags - allow new searches
      this.noProductShownForParams = null;
      this.isUpdatingUrl = false;
      
      // Reset product loading state
      this.allProducts = [];
      this.isLoadingProducts = false;
      
      // Clear the filter and show all products
      const productsListing = document.querySelector('#products-listing');
      let totalProducts = 0;
      
      if (productsListing) {
        const productCards = productsListing.querySelectorAll('[data-product-card]');
        productCards.forEach((card) => {
          // Skip promo blocks - they should always be visible
          if (!card.classList.contains('card-product-promo')) {
            card.style.display = '';
            card.classList.remove('dryer-kit-hidden');
            totalProducts++;
          }
        });
        
        // Ensure promo blocks are visible
        const promoBlocks = productsListing.querySelectorAll('.card-product-promo');
        promoBlocks.forEach((promoBlock) => {
          promoBlock.style.display = '';
          promoBlock.classList.remove('dryer-kit-hidden');
        });
      }
      
      // Hide message container
      this.hideMessageContainer();
      
      // Restore original product count or use the count of visible products
      const countToRestore = this.originalProductCount !== null ? this.originalProductCount : totalProducts;
      this.updateTotalProductsCount(countToRestore);
      
      // Show pagination
      this.showPagination();
      
      // Reset model and serial selections
      this.selectedModel = null;
      this.selectedSerial = null;
      
      // Reset model dropdown to default
      if (this.modelToggle) {
        const modelPlaceholder = this.modelToggle.querySelector('.option_txt');
        if (modelPlaceholder) {
          modelPlaceholder.textContent = 'Select Dryer Model';
        }
        // Reset to default option
        if (this.modelList) {
          const defaultOption = this.modelList.querySelector('[data-value=""]');
          if (defaultOption) {
            const defaultRadio = defaultOption.querySelector('input[type="radio"]');
            if (defaultRadio) {
              defaultRadio.checked = true;
            }
            this.modelList.querySelectorAll('li').forEach(item => item.classList.remove('selected'));
            defaultOption.classList.add('selected');
          }
        }
      }
      
      // Reset serial dropdown to default and disable it
      if (this.serialToggle) {
        this.serialToggle.disabled = true;
        this.serialToggle.setAttribute('aria-disabled', 'true');
        const serialPlaceholder = this.serialToggle.querySelector('.option_txt');
        if (serialPlaceholder) {
          serialPlaceholder.textContent = 'Select Serial Number';
        }
      }
      
      if (this.serialList) {
        this.serialList.innerHTML = '<li class="form-check selected" role="menuitem" tabindex="0" data-value=""><input type="radio" value="" class="sortby_options form-check-input" name="dryer-serial" id="serial-number-option-default" tabindex="-1" checked selected><label for="serial-number-option-default" class="option_txt form-check-label" title="Select your Serial Number">Select your Serial Number</label></li>';
      }
      
      // Rebind custom-select components after clearing
      // This ensures dropdowns work properly for the next search
      requestAnimationFrame(() => {
        if (this.modelSelect && typeof this.modelSelect.refreshCustomSelect === 'function') {
          this.modelSelect.refreshCustomSelect();
        }
        if (this.serialSelect && typeof this.serialSelect.refreshCustomSelect === 'function') {
          this.serialSelect.refreshCustomSelect();
        }
      });
      
      // Hide clear button
      if (this.clearBtn) {
        this.clearBtn.style.display = 'none';
      }
      
      // Clear URL parameters
      const url = new URL(window.location.href);
      url.searchParams.delete('dryer_model');
      url.searchParams.delete('dryer_serial');
      url.searchParams.delete('product_handle');
      window.history.pushState({}, '', url.toString());
    }
  }

  (function wrapDryerKitPrototype() {
    const proto = DryerKitFilter.prototype;
    Object.getOwnPropertyNames(proto).forEach((name) => {
      if (name === 'constructor') return;
      const desc = Object.getOwnPropertyDescriptor(proto, name);
      if (!desc || typeof desc.value !== 'function') return;
      const original = desc.value;
      proto[name] = function dkWrappedMethod(...args) {
        try {
          const result = original.apply(this, args);
          if (result != null && typeof result.then === 'function') {
            return result.catch((err) => {
              dkRollbar(name, err);
              return Promise.reject(err);
            });
          }
          return result;
        } catch (e) {
          dkRollbar(name, e);
          throw e;
        }
      };
    });
  })();

  // Populate .dryer-kit-data-metaobjects with all entries (all pages from section)
  async function fetchDryerKitSectionPage(root, sectionId, page, pageParam) {
    try {
      const param = pageParam || 'page';
      const url = `${root}?section_id=${sectionId}&${param}=${page}`;
      const response = await fetch(url);
      if (!response.ok) return null;
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const jsonScript = doc.querySelector('#dryer-kit-json-data');
      if (jsonScript && jsonScript.textContent) return JSON.parse(jsonScript.textContent);
      return null;
    } catch (e) {
      dkRollbar('fetchDryerKitSectionPage', e, { sectionId, page });
      return null;
    }
  }

  function buildMapsFromEntries(entries) {
    try {
    const serialMap = {};
    const productMap = {};
    entries.forEach(entry => {
      const model = (entry.model || '').trim();
      const serial = (entry.serial || '').trim();
      const productHandle = (entry.productHandle || '').trim();
      if (!model || !serial) return;
      if (!serialMap[model]) serialMap[model] = [];
      const exists = serialMap[model].some(s => (s.serial === serial));
      if (!exists) serialMap[model].push({ serial, productHandle });
      if (productHandle) productMap[`${model}|${serial}`] = productHandle;
    });
    Object.keys(serialMap).forEach(model => {
      serialMap[model].sort((a, b) =>
        (a.serial || '').localeCompare(b.serial || '', undefined, { numeric: true, sensitivity: 'base' })
      );
    });
    return { serialMap, productMap };
    } catch (e) {
      dkRollbar('buildMapsFromEntries', e);
      return { serialMap: {}, productMap: {} };
    }
  }

  async function getDryerKitMetaobjectsStore() {
    try {
    if (window._dryerKitMetaobjectsStorePromise) return window._dryerKitMetaobjectsStorePromise;
    const root = window.Shopify?.routes?.root || '/';
    const sectionId = 'dryer-kit-data';
    const allDataStrings = [];
    const first = await fetchDryerKitSectionPage(root, sectionId, 1, 'page');
    if (!first || !first.data) {
      const empty = { serialMap: {}, productMap: {}, entries: [], ready: true };
      window.dryerKitMetaobjectsStore = empty;
      window._dryerKitMetaobjectsStorePromise = Promise.resolve(empty);
      return window._dryerKitMetaobjectsStorePromise;
    }
    allDataStrings.push(first.data);
    const pageParam = first.pageParam || 'page';
    let totalPages = first.totalPages || 1;
    const totalItems = first.totalItems || 0;
    const FULL_PAGE_SIZE = 250;
    if (totalPages === 1 && totalItems > FULL_PAGE_SIZE) totalPages = Math.ceil(totalItems / FULL_PAGE_SIZE);
    if (totalPages > 1) {
      const batchSize = 5;
      for (let batchStart = 2; batchStart <= totalPages; batchStart += batchSize) {
        const batchEnd = Math.min(batchStart + batchSize - 1, totalPages);
        const promises = [];
        for (let p = batchStart; p <= batchEnd; p++) promises.push(fetchDryerKitSectionPage(root, sectionId, p, pageParam));
        const results = await Promise.all(promises);
        results.forEach(r => { if (r && r.data) allDataStrings.push(r.data); });
      }
    }
    let nextPageNum = 2;
    while (totalPages === 1 && first.entryCount === FULL_PAGE_SIZE && nextPageNum <= 20) {
      const nextPage = await fetchDryerKitSectionPage(root, sectionId, nextPageNum, pageParam);
      if (!nextPage || !nextPage.data) break;
      allDataStrings.push(nextPage.data);
      if (nextPage.totalPages && nextPage.totalPages > 1) totalPages = nextPage.totalPages;
      else if (nextPage.entryCount < FULL_PAGE_SIZE) break;
      nextPageNum += 1;
    }
    const combined = allDataStrings.filter(s => s && s.trim()).join('##');
    const rawEntries = combined.split('##').filter(e => e && e.trim());
    const seenIds = new Set();
    const entries = [];
    for (const raw of rawEntries) {
      const parts = raw.split('|');
      const id = (parts[0] || '').trim();
      const model = (parts.length >= 4 ? parts[1] : parts[0] || '').trim();
      const serial = (parts.length >= 4 ? parts[2] : parts[1] || '').trim();
      const productHandle = (parts.length >= 4 ? parts[3] : (parts.length >= 3 ? parts[2] : '')).trim();
      const key = id || `${model}|${serial}`;
      if (seenIds.has(key)) continue;
      seenIds.add(key);
      entries.push({ id: key, model, serial, productHandle });
    }
    const { serialMap, productMap } = buildMapsFromEntries(entries);
    const store = { serialMap, productMap, entries, ready: true };
    window.dryerKitMetaobjectsStore = store;
    window._dryerKitMetaobjectsStorePromise = Promise.resolve(store);
    return window._dryerKitMetaobjectsStorePromise;
    } catch (e) {
      dkRollbar('getDryerKitMetaobjectsStore', e);
      const empty = { serialMap: {}, productMap: {}, entries: [], ready: true, error: true };
      window.dryerKitMetaobjectsStore = empty;
      window._dryerKitMetaobjectsStorePromise = Promise.resolve(empty);
      return window._dryerKitMetaobjectsStorePromise;
    }
  }

  // Initialize when DOM is ready
  // Store instance globally so storefront-filters can access it
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      try {
        window.dryerKitFilterInstance = new DryerKitFilter();
      } catch (e) {
        dkRollbar('initDOMContentLoaded', e);
      }
    });
  } else {
    try {
      window.dryerKitFilterInstance = new DryerKitFilter();
    } catch (e) {
      dkRollbar('initImmediate', e);
    }
  }
})();
